<template>
    <modal size="lg" @close="reset(); $emit('close')" :height-max="true" ref="modal" :is-closeable="isCloseable" id="addEarningModal" :autoshow="true">
        <template v-slot:title>
            <h3 class="modal-title">Add Earning</h3>
        </template>

        <slot>
            <form-errors :errors="formErrors"/>

            <div v-if="dataLoaded">
                <div v-if="step == 'companies'">
                    <p class="mb-0">Select the company or companies these earnings will be available for.</p>
                    <company-selector :client="client" :companies="client.companies" v-model="formData.companies"/>
                </div>

                <div v-if="step == 'earning-types'">
                    <p>What earning(s) will be available?</p>
                    <earning-selector v-model="formData.earning_types" :availableEarnings="availableEarningTypes" @update:model-value="onUpdateSelectedEarningTypes($event)" :hideSearch="true" :hideTabs="true"/>
                </div>

                <div v-if="step == 'positions' && !editingEarningType">
                    <p>Assign positions to each earning</p>
                    <table class="table css-table widget-table table-fixed mb-2">
                        <thead>
                            <tr>
                                <th>Earning</th>
                                <th class="cw-6 text-right">Positions</th>
                            </tr>
                        </thead>
                        <tbody v-for="earningType in selectedEarningTypes">
                            <tr>
                                <td class="truncate-wrapper">
                                    <a href @click.prevent="editEarningTypePositions(earningType)" class="truncate-content">
                                        <span>{{ earningType.name }}</span>
                                    </a>
                                </td>
                                <td class="text-right">
                                    <span>{{ formData.positions_by_earning_type[earningType.id] ? formData.positions_by_earning_type[earningType.id].length : '' }}</span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div v-if="step == 'positions' && editingEarningType">
                    <p>Assign positions to each earning.</p>
                    <position-selector :all-positions="allCompanyPositions" :companies="client.companies" v-model="formData.positions_by_earning_type[editingEarningType.id]"/>
                </div>
            </div>
        </slot>

        <template v-slot:footer>
            <button-row stacked>
                <button v-if="step == 'companies'" type="button" class="btn btn-outline-primary" @click.prevent="close()">
                    Cancel
                </button>
                <button v-else-if="!editingEarningType" type="button" class="btn btn-outline-primary" @click.prevent="prevStep">
                    Back
                </button>

                <button v-if="step == 'positions' && editingEarningType" type="button" class="btn btn-primary" @click.prevent="onSubmitEarningType(editingEarningType.id); this.editingEarningType = null">
                    Save & Back
                </button>
                <button v-else-if="step == 'positions' && !editingEarningType" type="button" class="btn btn-primary" @click.prevent="onSubmitAllEarningTypes()">
                    Save & Exit
                </button>
                <button v-else type="button" class="btn btn-primary" @click.prevent="nextStep">
                    Continue
                </button>
            </button-row>
        </template>
    </modal>
</template>

<script>
import FormMixin from '@/mixins/Form'
import ModalMixin from '@/mixins/ModalMixin'
import PositionSelector from '@/pages/client/Settings/components/PositionSelector'
import CompanySelector from '@/pages/client/Settings/components/CompanySelector'
import EarningSelector from '@/pages/client/Settings/components/EarningSelector'

export default {
    props: ['client', ],
    mixins: [FormMixin, ModalMixin],
    components: {PositionSelector, CompanySelector, EarningSelector},
    data() {
        return {
            steps: ['companies', 'earning-types', 'positions'],
            step: 'companies',
            formData: this.makeFormData(),
            allPositions: [],
            positionsLoaded: false,
            earningTypesLoaded: false,
            editingEarningType: null
        }
    },
    mounted() {
        this.getPositions()
        this.getEarningTypes()
    },
    watch: {
        dataLoaded(val) {
            if (val) {
                this.$store.dispatch('STOP_LOADING')
            }
        }
    },
    computed: {
        dataLoaded() {
            return this.positionsLoaded && this.earningTypesLoaded
        },
        allCompanyPositions() {
            if (this.formData.companies) {
                return this.allPositions.filter(p => this.formData.companies.includes(p.company_id))
            }
            return []
        },
        availableEarningTypes() {
            return this.allEarningTypes // TODO KB CS filter this down to just earnings that are not already attached to at least 1 position for all companies for this client
        },
        selectedEarningTypes() {
            return this.allEarningTypes.filter(e => this.formData.earning_types.includes(e.id))
        },
        selectedEarningTableData() {
            const result = []

            this.selectedEarningTypes.forEach(e => {
                result.push({
                    id: e.id,
                    object: e,
                    cells: [
                        e.name,
                        ''
                    ],
                })
            })

            return [result]
        },
    },
    methods: {
        getPositions() {
            this.$store.dispatch('START_LOADING')
            this.$api.get(`/clients/${this.client.id}/positions`).then(resp => {
                this.allPositions = resp
                this.allPositions.sort((a, b) => {return a.terminal_value.toLowerCase() > b.terminal_value.toLowerCase() ? 1 : -1})
                this.positionsLoaded = true
            }).catch((errors) => {
                this.$bus.showError(errors.__all__)
            })
        },
        getEarningTypes() {
            this.$store.dispatch('START_LOADING')
            this.$api.get(`/admin/tax-engine/earning-types`).then(resp => {
                this.allEarningTypes = resp
                this.earningTypesLoaded = true
            }).catch((errors) => {
                this.$bus.showError(errors.__all__)
            })
        },
        makeFormData() {
            const data = {
                companies: [],
                earning_types: [],
                positions_by_earning_type: {}
            }

            return data
        },
        resetErrors() { // TODO KB consider adding this to FormMixin
            this.errors =  {}
            this.formErrors = []
        },
        nextStep() {
            this.step = this.steps[this.steps.indexOf(this.step)+1]
        },
        prevStep() {
            this.step = this.steps[this.steps.indexOf(this.step)-1]
        },
        onUpdateSelectedEarningTypes(earningTypeIds) {
            earningTypeIds.forEach(earningTypeId => {
                if (earningTypeId) {
                    this.formData.positions_by_earning_type[earningTypeId] = this.formData.positions_by_earning_type[earningTypeId] || []
                }
            })
        },
        editEarningTypePositions(earningType) {
            this.editingEarningType = earningType
        },
        getEarningTypeFormData(earningTypeId) {
            let positionsByEarningTypeToSubmit = Object.fromEntries(
                Object.entries(this.formData.positions_by_earning_type).filter(([key]) => key == earningTypeId)
            )
            return {
                companies: this.formData.companies,
                earning_types: [earningTypeId],
                positions_by_earning_type: positionsByEarningTypeToSubmit
            }
        },
        onSubmitEarningType(earningTypeId) {
            this.$store.dispatch('START_LOADING')
            this.resetErrors()
            return this.$api.post(`/clients/${this.client.id}/settings/earning-types`, this.getEarningTypeFormData(earningTypeId)).then(resp => {
                this.$store.dispatch('STOP_LOADING')
            }).catch(errors => {
                this.$store.dispatch('STOP_LOADING')
                this.errors = errors
                this.formErrors = errors.__all__
                this.onError()
            })
        },
        onSubmitAllEarningTypes() {
            this.$store.dispatch('START_LOADING')
            this.resetErrors()
            return this.$api.post(`/clients/${this.client.id}/settings/earning-types`, this.getFormData()).then(resp => {
                this.$store.dispatch('STOP_LOADING')
                this.$emit('updated', resp)
                this.close()
            }).catch(errors => {
                this.$store.dispatch('STOP_LOADING')
                this.errors = errors
                this.formErrors = errors.__all__
                this.onError()
            })
        }
    }
}
</script>
